import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

export default function Account() {
  const { user, setUser, logout } = useAuth();
  const navigate = useNavigate();
  const [prefs, setPrefs] = useState({ monday_vigil: false, morning_reminder: false, evening_reminder: false });
  const [tz, setTz] = useState("UTC");
  const [journal, setJournal] = useState([]);
  const [confirmDelete, setConfirmDelete] = useState(false);

  useEffect(() => {
    if (user) {
      setPrefs({ monday_vigil: false, morning_reminder: false, evening_reminder: false, ...(user.email_preferences || {}) });
      setTz(user.timezone || "UTC");
    }
    api.get("/me/journal").then(({ data }) => setJournal(data.entries)).catch(() => {});
  }, [user]);

  const savePrefs = async () => {
    const { data } = await api.patch("/auth/preferences", { email_preferences: prefs, timezone: tz });
    setUser(data);
    toast("Preferences saved.");
  };

  const deleteAccount = async () => {
    await api.delete("/auth/account");
    toast("Your account has been deleted.");
    await logout();
    navigate("/");
  };

  const toggle = (k) => setPrefs((p) => ({ ...p, [k]: !p[k] }));

  return (
    <div className="max-w-2xl mx-auto px-6 md:px-10 py-20">
      <p className="mono-label mb-4">My Account</p>
      <h1 className="font-heading text-4xl md:text-5xl text-ink leading-none">{user?.name}</h1>
      <p className="font-body text-ink-secondary mt-3">{user?.email}</p>

      <section className="mt-14 paper-rule pt-10">
        <h2 className="font-heading text-2xl text-ink mb-6">Practice & reminders</h2>
        <p className="font-body text-ink-secondary mb-6 leading-relaxed">All reminders are optional. No streaks, no badges, no guilt.</p>
        <div className="space-y-4">
          {[["monday_vigil", "Receive the Monday Vigil email"], ["morning_reminder", "Morning Prayer reminder"], ["evening_reminder", "Evening Prayer reminder"]].map(([k, label]) => (
            <label key={k} className="flex items-center gap-3 cursor-pointer">
              <input type="checkbox" checked={!!prefs[k]} onChange={() => toggle(k)} className="w-4 h-4 accent-ink" data-testid={`pref-${k}`} />
              <span className="font-body text-ink">{label}</span>
            </label>
          ))}
        </div>
        <label className="block mt-6"><span className="mono-label block mb-2">Timezone</span>
          <input value={tz} onChange={(e) => setTz(e.target.value)} className="bg-transparent border border-ink/25 px-3 py-2 font-body text-ink outline-none w-full max-w-xs" data-testid="pref-tz" /></label>
        <button onClick={savePrefs} className="btn-solid mt-6" data-testid="pref-save">Save preferences</button>
      </section>

      <section className="mt-14 paper-rule pt-10">
        <h2 className="font-heading text-2xl text-ink mb-6">My gratitude journal</h2>
        {journal.length === 0 ? (
          <p className="font-body text-ink-tertiary italic">Your private reflections will appear here.</p>
        ) : (
          <div className="space-y-4">
            {journal.map((e) => (
              <div key={e.id} className="border-l-2 border-sage pl-4" data-testid={`journal-${e.id}`}>
                <span className="mono-label">{e.period} · {new Date(e.date).toLocaleDateString()}</span>
                <p className="font-body text-ink mt-1 leading-relaxed">{e.entry}</p>
              </div>
            ))}
          </div>
        )}
      </section>

      <section className="mt-14 paper-rule pt-10">
        <h2 className="font-heading text-2xl text-ink mb-4">Delete account</h2>
        <p className="font-body text-ink-secondary mb-5 leading-relaxed">This removes your account, held prayers and private journal. It cannot be undone.</p>
        {confirmDelete ? (
          <div className="flex gap-4">
            <button onClick={deleteAccount} className="btn-solid" style={{ background: "#D34E24", borderColor: "#D34E24" }} data-testid="delete-confirm">Yes, delete everything</button>
            <button onClick={() => setConfirmDelete(false)} className="btn-ink">Keep my account</button>
          </div>
        ) : (
          <button onClick={() => setConfirmDelete(true)} className="btn-ink" data-testid="delete-account">Delete my account</button>
        )}
      </section>
    </div>
  );
}
